import { useEffect, useState } from "react";
import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard,
  FileText,
  Hammer,
  Newspaper,
  CalendarDays,
  Video,
  Images,
  Download,
  Quote,
  History,
  BarChart3,
  FolderTree,
  BookOpen,
  Mail,
  Settings,
  Menu,
  X,
  LogOut,
  Loader2,
} from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/use-admin-auth";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin")({
  component: AdminLayout,
});

const NAV = [
  { to: "/admin", label: "Painel", icon: LayoutDashboard, exact: true },
  { to: "/admin/propostas", label: "Propostas", icon: FileText },
  { to: "/admin/trabalhos", label: "Trabalhos", icon: Hammer },
  { to: "/admin/noticias", label: "Notícias", icon: Newspaper },
  { to: "/admin/agenda", label: "Agenda", icon: CalendarDays },
  { to: "/admin/videos", label: "Vídeos", icon: Video },
  { to: "/admin/galerias", label: "Galerias", icon: Images },
  { to: "/admin/materiais", label: "Materiais", icon: Download },
  { to: "/admin/depoimentos", label: "Depoimentos", icon: Quote },
  { to: "/admin/trajetoria", label: "Trajetória", icon: History },
  { to: "/admin/numeros", label: "Números", icon: BarChart3 },
  { to: "/admin/categorias", label: "Categorias", icon: FolderTree },
  { to: "/admin/paginas", label: "Páginas", icon: BookOpen },
  { to: "/admin/mensagens", label: "Mensagens", icon: Mail },
  { to: "/admin/configuracoes", label: "Configurações", icon: Settings },
];

function AdminLayout() {
  const { session, user, isAdmin, loading } = useAdminAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    if (!loading && !session) {
      navigate({ to: "/auth" });
    }
  }, [loading, session, navigate]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  };

  if (loading || !session) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4 text-center">
        <div className="max-w-md space-y-3">
          <h1 className="font-display text-2xl text-foreground">Acesso restrito</h1>
          <p className="text-muted-foreground">
            Sua conta ({user?.email}) não tem permissão de administrador para acessar este painel.
          </p>
          <Button variant="outline" onClick={handleLogout}>
            <LogOut className="mr-2 size-4" /> Sair
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/40 lg:flex">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed z-50 flex h-screen w-72 flex-col bg-sidebar text-sidebar-foreground transition-transform duration-300 lg:sticky lg:top-0 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b border-sidebar-border px-5 py-5">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-full bg-accent-gradient font-display text-lg text-accent-foreground">
              40
            </div>
            <div>
              <p className="font-display text-sm leading-tight text-sidebar-foreground">Painel Admin</p>
              <p className="text-xs text-sidebar-foreground/60">Rurdiney 40123</p>
            </div>
          </div>
          <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
            <X className="size-5" />
          </button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {NAV.map((item) => {
            const active = item.exact ? pathname === item.to : pathname.startsWith(item.to);
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  active
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground/70 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground"
                }`}
              >
                <Icon className="size-4 shrink-0" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-sidebar-border p-4">
          <p className="mb-2 truncate text-xs text-sidebar-foreground/60">{user?.email}</p>
          <Button variant="outline" size="sm" className="w-full border-sidebar-border bg-transparent text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground" onClick={handleLogout}>
            <LogOut className="mr-2 size-4" /> Sair
          </Button>
        </div>
      </aside>

      <div className="flex min-h-screen flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-card px-4 py-3 lg:hidden">
          <button onClick={() => setSidebarOpen(true)} className="text-foreground">
            <Menu className="size-6" />
          </button>
          <span className="font-display text-sm text-foreground">Painel Admin</span>
          <span className="w-6" />
        </header>
        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
